import { useCallback } from 'react';
import { DocumentType, Supplier, Supply } from '@bulthaup/api/db';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconTruck } from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { RouterOutput, trpc } from '../../../../../../services/trpc';

// Zod schema for validation
const deliveryOrderSchema = z.object({
  selectedSupplies: z
    .array(z.string())
    .min(1, 'Sélectionnez au moins une fourniture'),
  deliveryTime: z
    .string()
    .min(1, 'Veuillez sélectionner une heure de livraison'),
  access: z.string().min(1, "Veuillez renseigner les informations d'accès"),
  deliveryContactUserId: z.string().optional(),
});

export type DeliveryOrderFormValues = z.infer<typeof deliveryOrderSchema>;

type SupplyWithSupplier = Supply & { supplier: Supplier | null };

export default function DeliveryOrderForm({
  project,
  supplies,
  onSubmit,
  onCancel,
}: {
  project: NonNullable<RouterOutput['projects']['get']>;
  supplies: SupplyWithSupplier[];
  onSubmit: (values: DeliveryOrderFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const { data: technicians } = trpc.users.listTechnicians.useQuery();

  const deliveryOrderDocuments = project.documents.filter(
    document => document.type === DocumentType.DELIVERY_ORDER,
  );
  const isFirstDeliveryOrder = deliveryOrderDocuments.length === 0;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<DeliveryOrderFormValues>({
    resolver: zodResolver(deliveryOrderSchema),
    defaultValues: {
      selectedSupplies: isFirstDeliveryOrder
        ? supplies.map(supply => supply.id)
        : [],
      deliveryTime: '',
      access: '',
      deliveryContactUserId: '',
    },
  });

  const watchedSupplies = watch('selectedSupplies');
  const watchedDeliveryTime = watch('deliveryTime');
  const watchedAccess = watch('access');

  const handleFormSubmit = useCallback(
    async (values: DeliveryOrderFormValues) => {
      await onSubmit(values);
    },
    [onSubmit],
  );

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack gap="xl">
        {/* Header */}
        <Title order={3}>Créer un ordre de livraison</Title>

        {/* Form fields */}
        <Group grow>
          <Controller
            name="deliveryTime"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Heure de livraison"
                placeholder="Ex: 14:30"
                error={errors.deliveryTime?.message}
                required
              />
            )}
          />
          <Controller
            name="access"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Informations d'accès"
                placeholder="Code d'accès, étage, instructions..."
                error={errors.access?.message}
                required
              />
            )}
          />
          <Controller
            name="deliveryContactUserId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Contact de livraison"
                placeholder="Client"
                clearable
                data={(technicians ?? []).map(t => ({
                  value: t.id,
                  label: `${t.firstName} ${t.lastName}${t.phone ? ` - ${t.phone}` : ''}`,
                }))}
              />
            )}
          />
        </Group>

        {/* Error message */}
        {errors.selectedSupplies && (
          <Text c="red" size="sm">
            {errors.selectedSupplies.message}
          </Text>
        )}

        {/* Supplies list */}
        <Stack gap="xs">
          {supplies.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 200px 1fr 120px',
                gap: '8px',
                alignItems: 'center',
                paddingBottom: '8px',
                borderBottom: '1px solid #e9ecef',
              }}
            >
              <div></div>
              <Text size="sm" fw={600} c="dimmed">
                Fournisseur
              </Text>
              <Text size="sm" fw={600} c="dimmed">
                Article
              </Text>
              <Text size="sm" fw={600} c="dimmed">
                Référence
              </Text>
            </div>
          )}
          {supplies.map(supply => (
            <Controller
              key={supply.id}
              name="selectedSupplies"
              control={control}
              render={({ field }) => {
                const isChecked = field.value.includes(supply.id);
                const handleToggle = () => {
                  if (isChecked) {
                    field.onChange(
                      field.value.filter((id: string) => id !== supply.id),
                    );
                  } else {
                    field.onChange([...field.value, supply.id]);
                  }
                };

                return (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 200px 1fr 120px',
                      gap: '8px',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '4px 0',
                      borderRadius: '4px',
                      backgroundColor: isChecked ? '#f8f9fa' : 'transparent',
                    }}
                    onClick={handleToggle}
                  >
                    <Checkbox
                      checked={isChecked}
                      onChange={() => {}} // Handled by row click
                      onBlur={field.onBlur}
                      size="sm"
                      style={{ pointerEvents: 'none' }}
                    />
                    <Text size="sm" fw={500}>
                      {supply.supplier?.name || 'N/A'}
                    </Text>
                    <Text size="sm">{supply.itemName}</Text>
                    <Text size="sm" c="dimmed">
                      {supply.reference || '-'}
                    </Text>
                  </div>
                );
              }}
            />
          ))}
        </Stack>

        {supplies.length === 0 && (
          <Text c="dimmed" ta="center" size="sm">
            Aucune fourniture disponible pour ce projet.
          </Text>
        )}

        {/* Action buttons */}
        <Group justify="space-between" mt="xl">
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Annuler
          </Button>

          <Button
            leftSection={<IconTruck size={16} />}
            type="submit"
            disabled={
              watchedSupplies.length === 0 ||
              !watchedDeliveryTime ||
              !watchedAccess
            }
            loading={isSubmitting}
          >
            Créer l'ordre de livraison
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
